import {
  ComponentPropsWithoutRef,
  ElementRef,
  FC,
  forwardRef,
  PropsWithChildren,
} from 'react'

import { Root, Item, Header, Trigger, Content } from '@radix-ui/react-accordion'

import { IconArrowDown } from '~/components/ui/icons/arrow-down'
import { cn, twMerge } from '~/lib/utils'

type AccordionProps = PropsWithChildren<{
  triggerText: { close: string; open: string }
  wrapperClassName?: string
  contentClassName?: string
}>

export const Accordion: FC<AccordionProps> = ({
  triggerText,
  wrapperClassName,
  contentClassName,
  children,
}) => {
  return (
    <AccordionRoot className={wrapperClassName} collapsible type="single">
      <AccordionItem className="group" value="item-1">
        <AccordionTrigger>
          <span className="group-[[data-state=open]]:hidden">
            {triggerText.close}
          </span>
          <span className="group-[[data-state=closed]]:hidden">
            {triggerText.open}
          </span>
        </AccordionTrigger>
        <AccordionContent className={contentClassName}>
          {children}
        </AccordionContent>
      </AccordionItem>
    </AccordionRoot>
  )
}

const AccordionRoot = Root

const AccordionItem = Item

const AccordionTrigger = forwardRef<
  ElementRef<typeof Trigger>,
  ComponentPropsWithoutRef<typeof Trigger>
>(({ className, children, ...props }, ref) => (
  <Header className="flex">
    <Trigger
      className={cn(
        'group flex w-full items-center justify-center gap-x-1 bg-grayBg py-[14px] text-body-s [&[data-state=open]>svg]:rotate-180',
        className,
      )}
      ref={ref}
      {...props}
    >
      {children}
      <IconArrowDown />
    </Trigger>
  </Header>
))
AccordionTrigger.displayName = Trigger.displayName

const AccordionContent = forwardRef<
  ElementRef<typeof Content>,
  ComponentPropsWithoutRef<typeof Content>
>(({ children, className, ...props }, ref) => (
  <Content
    className={twMerge(
      'overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
      className,
    )}
    ref={ref}
    {...props}
  >
    {children}
  </Content>
))

AccordionContent.displayName = Content.displayName
